import { Box, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLoginUserMutation } from "../../redux/api/authApi";
import logo from "../../assets/logo_verify.svg";
import FormInput from "../../components/FormInput";
import FormInputPassword from "../../components/FormInputPassword";
import { useIntl } from "react-intl";

const LinkItem = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.secondary.main, // Accediendo al color del tema
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const loginSchema = object({
  email: string()
    .min(1, "La dirección de correo electrónico es requerida")
    .email("Debe indicar una dirección de correo electrónico"),
  password: string()
    .min(1, "La contraseña es requerida")
    .min(8, "La contraseña debe tener más de 8 caracteres")
    .max(32, "La contraseña debe tener menos de 32 caracteres"),
});

export type LoginInput = {
  email: string;
  password: string;
  general?: {
    message: string;
    type: string;
  };
};

const LoginPage = () => {
  const { formatMessage } = useIntl();
  const methods = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  // 👇 API Login Mutation
  const [loginUser, { isLoading, isError, error, isSuccess }] =
    useLoginUserMutation();

  const navigate = useNavigate();
  const location = useLocation();

  const from =
    ((location.state as any)?.from.pathname as string) || "/";

  const {
    handleSubmit,
    setError,
    formState: { isSubmitSuccessful },
  } = methods;

  useEffect(() => {
    if (isSuccess) {
      navigate(from);
    }

    if (isError && error) {
      if ("status" in error && error.status === 401) {
        // Limpia los tokens almacenados
        localStorage.removeItem("refresh_token");
        document.cookie = "refresh_token=; Max-Age=0";
        localStorage.removeItem("access_token");
        document.cookie = "access_token=; Max-Age=0";
        // Manejo del error por tipo
        if (error.data && typeof error.data === "object" && "message" in error.data && error.data.message === "Company is deactivated") {
          toast.error("La empresa está desactivada. Contacta al administrador.");
        } else {
          setError("password", {
            type: "manual",
            message: "Las credenciales introducidas son erróneas",
          });
        }
      }
    }
  }, [isSuccess, isError]);

  const onSubmitHandler: SubmitHandler<LoginInput> = (values) => {
    // 👇 Executing the loginUser Mutation
    loginUser(values);
  };

  return (

    <Box
      sx={{
        width: "80%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "3rem",
        "@media (min-width:900px)": {
          width: "80%", // Para md
        },
        "@media (min-width:1200px)": {
          width: "60%",
        },
        "@media (min-width:1536px)": {
          width: "60%", // Para xl
        },
      }}
    >
      <Box
        sx={{
          width: "40%",
          marginBottom: "3rem",
          "@media (min-width:900px)": {
            width: "90%", // Para md
          },
          "@media (min-width:1200px)": {
            width: "80%",
          },
          "@media (min-width:1536px)": {
            width: "70%", // Para xl
          },
        }}
      >
        <img src={logo} className="img-responsive" alt="Logo" />
      </Box>
      <Box sx={{ width: "100%" }}>
        <Typography
          component="h1"
          color="primary"
          sx={{
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "1.8125rem",
            width: "100%",
            mb: ".3rem",
            fontSize: { xs: "1.4rem", xl: "1.4rem" },
          }}
        >
          {formatMessage({ id: "login.title" })}
        </Typography>
        <Typography
          variant="body1"
          component="h2"
          sx={{
            color: "#A7ABBB",
            mb: 4,
            fontSize: { xs: ".8rem", xl: ".8rem" },
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "0.9375rem" /* 93.75% */,
          }}
        >
          {formatMessage({ id: "login.subtitle" })}
        </Typography>
      </Box>

      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmitHandler)}
          noValidate
          autoComplete="off"
          width="100%"
        >
          {methods.formState.errors && methods.formState.errors.general?.message && (
            <span>{typeof methods.formState.errors.general.message === "string" ? methods.formState.errors.general.message : ''}</span>
          )}

          <FormInput
            name="email"
            label={formatMessage({ id: "login.label.email" })}
            type="email"
            autoComplete="email"
            sx={{
              mb: 2,
              ".MuiTypography-body2": {
                fontWeight: 600,
                fontSize: ".9rem",
                fontStyle: "normal",
                lineHeight: "1.8125rem",
              },
              ".MuiInput-root": {
                fontSize: ".9rem",
                padding: 0,
                height: "32px",
                lineHeight: "1.8125rem",
                color: "#718096",
              },
            }}
          />
          <FormInputPassword
            name="password"
            label={formatMessage({ id: "login.label.password" })}

            autoComplete="current-password"
            sx={{
              mt: 1,
              ".MuiTypography-body2": {
                fontWeight: 600,
                fontSize: ".9rem",
                fontStyle: "normal",
                lineHeight: "1.8125rem",
              },
              ".MuiInput-root": {
                fontSize: ".9rem",
                padding: 0,
                height: "32px",
                lineHeight: "1.8125rem",
                color: "#718096",
              },
            }}
          />

          <LinkItem
            to="/forgot-password"
            sx={{
              marginTop: "1rem",
              fontSize: ".875rem",
              letterSpacing: "0.04375rem",
            }}
          >
            {formatMessage({ id: "login.forgot" })}
          </LinkItem>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              sx={{ mt: 3, width: "100%", fontSize: "1.1rem" }}
              disableElevation
              type="submit"
            >
              {formatMessage({ id: "login.button" })}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default LoginPage;
